/**
 * Centralized Analytics Manager
 * Professional event system for multi-provider analytics
 */

import GTMProvider from '~/providers/gtm-provider.js';
import FacebookPixelProvider from '~/providers/facebook-pixel-provider.js';
import HotjarProvider from '~/providers/hotjar-provider.js';
import MicrosoftClarityProvider from '~/providers/microsoft-clarity-provider.js';
import GoogleOptimizeProvider from '~/providers/google-optimize-provider.js';

/**
 * Register all analytics providers
 */
function registerProviders(analyticsManager) {
  // Register GTM Provider (now centralized)
  analyticsManager.registerProvider('gtm', new GTMProvider());

  // Register other providers
  analyticsManager.registerProvider('facebook', new FacebookPixelProvider());
  analyticsManager.registerProvider('hotjar', new HotjarProvider());
  analyticsManager.registerProvider('clarity', new MicrosoftClarityProvider());
  analyticsManager.registerProvider('googleOptimize', new GoogleOptimizeProvider());

  console.log('[Analytics Manager] All providers registered');
}

class AnalyticsManager {
  constructor() {
    this.providers = new Map();
    this.eventQueue = [];
    this.isInitialized = false;
    this.config = null;
    this.propertyContext = null;

    console.log('[Analytics Manager] Initialized');
  }

  /**
   * Register an analytics provider
   */
  registerProvider(name, provider) {
    if (typeof provider.track !== 'function') {
      console.error(`[Analytics Manager] Provider ${name} must implement track() method`);
      return;
    }

    this.providers.set(name, {
      instance: provider,
      enabled: false,
      initialized: false
    });

    console.log(`[Analytics Manager] Provider '${name}' registered`);
  }

  /**
   * Configure analytics with backend data
   */
  configure(analyticsConfig, propertyData) {
    this.config = analyticsConfig;
    this.propertyContext = this.buildPropertyContext(propertyData);

    // Initialize enabled providers
    for (const [name, provider] of this.providers) {
      const providerConfig = analyticsConfig[name];

      if (providerConfig && providerConfig.enabled) {
        provider.enabled = true;

        if (typeof provider.instance.initialize === 'function') {
          try {
            provider.instance.initialize(providerConfig, this.propertyContext);
            provider.initialized = true;
            console.log(`[Analytics Manager] Provider '${name}' initialized`);
          } catch (error) {
            console.error(`[Analytics Manager] Failed to initialize '${name}':`, error);
          }
        }
      }
    }

    this.isInitialized = true;

    // Process queued events
    this.processEventQueue();

    console.log('[Analytics Manager] Configuration complete');
  }

  /**
   * Track an analytics event across all enabled providers
   */
  track(eventName, eventData = {}) {
    const enhancedEvent = this.enhanceEvent(eventName, eventData);

    // If not initialized, queue the event
    if (!this.isInitialized) {
      this.eventQueue.push({ eventName, eventData: enhancedEvent });
      console.log(`[Analytics Manager] Event '${eventName}' queued (not initialized)`);
      return;
    }

    // Send to all enabled providers
    for (const [name, provider] of this.providers) {
      if (provider.enabled && provider.initialized) {
        try {
          provider.instance.track(eventName, enhancedEvent);
        } catch (error) {
          console.error(`[Analytics Manager] Provider '${name}' tracking failed:`, error);
        }
      }
    }

    console.log(`[Analytics Manager] Event '${eventName}' sent to ${this.getEnabledProviderCount()} providers`);
  }

  /**
   * Enhance event with property context and timestamps
   */
  enhanceEvent(eventName, eventData) {
    return {
      ...eventData,
      timestamp: Date.now(),
      event_name: eventName,
      property_context: this.propertyContext,
      session_id: this.getSessionId(),
      page_url: typeof window !== 'undefined' ? window.location.href : null,
      page_title: typeof document !== 'undefined' ? document.title : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null
    };
  }

  /**
   * Build property context from property data
   */
  buildPropertyContext(propertyData) {
    if (!propertyData) return null;

    return {
      property_id: propertyData.id,
      property_name: propertyData.public_name || propertyData.name || '',
      property_brand: propertyData.brand || propertyData.group_name || '',
      property_type: propertyData.property_type || 'hotel',
      location: {
        country: propertyData.country || '',
        city: propertyData.city || '',
        region: propertyData.region || ''
      }
    };
  }

  /**
   * Process queued events after initialization
   */
  processEventQueue() {
    while (this.eventQueue.length > 0) {
      const { eventName, eventData } = this.eventQueue.shift();
      this.track(eventName, eventData);
    }
  }

  /**
   * Get session ID (create if doesn't exist)
   */
  getSessionId() {
    if (typeof window === 'undefined') return null;

    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Get count of enabled providers
   */
  getEnabledProviderCount() {
    return Array.from(this.providers.values()).filter(p => p.enabled && p.initialized).length;
  }

  /**
   * Update consent settings for all providers
   */
  updateConsent(preferences) {
    // Store consent globally for provider access
    if (typeof window !== 'undefined') {
      window.analyticsConsent = preferences;
    }

    // Notify all providers about consent changes
    for (const [name, provider] of this.providers) {
      if (provider.enabled && provider.initialized && typeof provider.instance.updateConsent === 'function') {
        try {
          provider.instance.updateConsent(preferences);
        } catch (error) {
          console.error(`[Analytics Manager] Provider '${name}' consent update failed:`, error);
        }
      }
    }

    console.log(`[Analytics Manager] Consent updated for ${this.getEnabledProviderCount()} providers`, preferences);
  }

  /**
   * Get provider status for debugging
   */
  getStatus() {
    const status = {
      initialized: this.isInitialized,
      queuedEvents: this.eventQueue.length,
      config: this.config,
      propertyContext: this.propertyContext,
      providers: {}
    };

    for (const [name, provider] of this.providers) {
      status.providers[name] = {
        enabled: provider.enabled,
        initialized: provider.initialized
      };
    }

    return status;
  }
}

// Create global instance
let analyticsManager = null;

export default function ({ app, store }) {
  // Create analytics manager instance
  analyticsManager = new AnalyticsManager();

  // Register all providers
  registerProviders(analyticsManager);

  // Make it globally available
  if (typeof window !== 'undefined') {
    window.$analytics = analyticsManager;
  }

  // Build analytics config from network data
  const buildAnalyticsConfig = (networkData) => {
    if (!networkData) return null;

    return {
      gtm: {
        enabled: !!networkData.google_tag_manager_id,
        container_id: networkData.google_tag_manager_id
      },
      ga4: {
        enabled: !!networkData.google_analytics_id,
        measurement_id: networkData.google_analytics_id
      },
      google_ads: {
        enabled: !!networkData.google_ads_conversion_id,
        conversion_id: networkData.google_ads_conversion_id,
        conversion_label: networkData.google_ads_conversion_label
      },
      facebook: {
        enabled: !!networkData.facebook_pixel_id,
        pixel_id: networkData.facebook_pixel_id
      },
      hotjar: {
        enabled: !!networkData.hotjar_site_id,
        site_id: networkData.hotjar_site_id
      },
      clarity: {
        enabled: !!networkData.microsoft_clarity_id,
        project_id: networkData.microsoft_clarity_id
      },
      googleOptimize: {
        enabled: !!networkData.google_optimize_id,
        container_id: networkData.google_optimize_id
      }
    };
  };

  // Watch for configuration changes
  const watchForConfig = () => {
    store.watch(
      (state) => state['booking-networks']?.getBookingNetworksRequest?.data,
      (networkData) => {
        const analyticsConfig = buildAnalyticsConfig(networkData);
        if (analyticsConfig) {
          const propertyData = store.state.search?.singleRequest?.data;
          analyticsManager.configure(analyticsConfig, propertyData);
          console.log('[Analytics Manager] Configured with network data');
        }
      },
      { deep: true }
    );

    // Also watch for property changes
    store.watch(
      (state) => state.search?.singleRequest?.data,
      (propertyData) => {
        if (propertyData && analyticsManager.config) {
          analyticsManager.configure(analyticsManager.config, propertyData);
        }
      },
      { deep: true }
    );
  };

  // Try immediate configuration
  const networkData = store.state['booking-networks']?.getBookingNetworksRequest?.data;
  const propertyData = store.state.search?.singleRequest?.data;
  const analyticsConfig = buildAnalyticsConfig(networkData);

  if (analyticsConfig) {
    analyticsManager.configure(analyticsConfig, propertyData);
    console.log('[Analytics Manager] Configured immediately with network data');
  } else {
    console.log('[Analytics Manager] Waiting for network data...');
    watchForConfig();
  }

  // Add to Nuxt context
  app.$analytics = analyticsManager;
}

// Export for direct usage
export { analyticsManager };